import { CommonModule } from "@angular/common"
import { Component, OnDestroy, OnInit } from "@angular/core"
import { Router } from "@angular/router"
import { BehaviorSubject, Observable, Subscription } from "rxjs"
import { DateTimePickerComponent, FileInputComponent } from "src/app/components"
import {
    ApplicationState,
    Application,
    ContractGenerate,
    ContractGenerateFactory,
    FileContent,
    ContractPrefix,
} from "src/app/models"
import { ContextService, KngService, LoadingService } from "src/app/services"
import { ToastService } from "src/app/services"
import { environment } from "src/environments/environment"
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner"
enum ViewState {
    Date = 0,
    Preview = 1,
}

@Component({
    selector: "valben-kng-generate",
    templateUrl: "./kng-generate.page.html",
    styles: [`
        :host {
            width: 100%;
            display: flex;
            flex-direction: column;
        }
    `],
    standalone: true,
    imports: [
        MatProgressSpinnerModule,
        CommonModule,
        DateTimePickerComponent,
        FileInputComponent,
    ],
})
export class KngGeneratePage implements OnInit, OnDestroy {
    itemsSubscription: Subscription | undefined = undefined
    loading$ = this._loadingService.kngLoading()

    viewStateEnum = ViewState
    viewState: ViewState = ViewState.Date
    applications: Application[] = []
    selectedApplication: Application | undefined = undefined
    minStartDate: Date = new Date()
    minEndDate: Date | undefined = this.minStartDate
    maxEndDate: Date | undefined = undefined

    private _date$ = new BehaviorSubject<{ startDate: Date | undefined, endDate: Date | undefined }>({
        startDate: undefined,
        endDate: undefined,
    })
    date$ = () => this._date$.asObservable()

    docxInput: boolean[] = [false, false, false]

    private _leaseInput$ = new BehaviorSubject<{ blob: FileContent } | undefined>(undefined)
    private _subleaseInput$ = new BehaviorSubject<{ blob: FileContent } | undefined>(undefined)
    private _mutualInput$ = new BehaviorSubject<{ blob: FileContent } | undefined>(undefined)
    contractInputs$ = [
        () => this._leaseInput$.asObservable(),
        () => this._subleaseInput$.asObservable(),
        () => this._mutualInput$.asObservable(),
    ]
    contractUpdates = [
        (blob: FileContent) => { this._leaseInput$.next({ blob: blob }) },
        (blob: FileContent) => { this._subleaseInput$.next({ blob: blob }) },
        (blob: FileContent) => { this._mutualInput$.next({ blob: blob }) },
    ]
    contractNames = ["Ugovor o najmu", "Ugovor o podnajmu", "Ugovor o uređenju međuljudskim odnosa"]

    constructor(
        private _contextService: ContextService,
        private _loadingService: LoadingService,
        private _router: Router,
        private _kngService: KngService,
        private _messageService: ToastService,
    ) {}

    ngOnInit(): void {
        this.itemsSubscription = this._contextService.getKngSubject()?.subscribe(applications => {
            this.applications = applications.filter((app: any) =>
                app.state === ApplicationState.Approved && app.landlordData?.submitted === true
            )
        })
    }

    ngOnDestroy(): void {
        this.itemsSubscription?.unsubscribe()
    }

    back(): void {
        this._router.navigate(["/kng-approved"])
    }

    selectApplication(application: Application): void {
        if (this.selectedApplication?.masterId === application.masterId) {
            return
        }
        this.selectedApplication = application
        this.minEndDate = this.minStartDate
        this.maxEndDate = undefined
        this._date$.next({ startDate: undefined, endDate: undefined })
        this.viewState = ViewState.Date
    }

    updateStartDate = (date: string) => {
        this.maxEndDate = new Date(date)
        this.maxEndDate.setFullYear(this.maxEndDate.getFullYear() + 4)
        this.maxEndDate.setDate(this.maxEndDate.getDate() - 1)

        this.minEndDate = new Date(date)
        this.minEndDate.setDate(new Date(date).getDate() + 1)

        this._date$.next({ startDate: new Date(date), endDate: this.maxEndDate })
    }

    updateEndDate = (date: string) => {
        this._date$.next({ startDate: this._date$.value.startDate, endDate: new Date(date) })
    }

    openPdf(idx: number): void {
        const prefixes: ContractPrefix[] = ["rental_agreement", "subrental_agreement", "regulation_of_mutual_relations"]
        this._kngService.PreviewContract(
            this.selectedApplication?.masterId ?? "",
            prefixes[idx],
            this._date$.value
        )
    }

    previewContracts(): void {
        this.viewState = ViewState.Preview
    }

    removeContract(idx: number): void {
        this.docxInput[idx] = true
    }

    downloadContract(idx: number): void {
        const prefixes: ContractPrefix[] = ["rental_agreement", "subrental_agreement", "regulation_of_mutual_relations"]
        this._kngService.DownloadContract(
            this.selectedApplication?.masterId ?? "",
            prefixes[idx],
            this._date$.value
        )
    }

    generateContracts(): void {
        const body: ContractGenerate = {
            leaseDocx: this._leaseInput$.value?.blob.base64.split(",")[1] ?? "",
            subleaseDocx: this._subleaseInput$.value?.blob.base64.split(",")[1] ?? "",
            mutualRelationsDocx: this._mutualInput$.value?.blob.base64.split(",")[1] ?? "",
            startDate: this._date$.value.startDate ?? new Date(),
            endDate: this._date$.value.endDate ?? new Date(),
        }
        this._contextService.generateContracts(
            this.selectedApplication?.masterId ?? "", body
        ).subscribe({
            next: () => this._router.navigate(["/kng-approved"]),
            error: err => {
                const serverMsg = err?.error?.message ?? err?.error
                const detail = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("start date")
                    ? "Datum početka mora biti u budućnosti."
                    : "Došlo je do greške prilikom generiranja ugovora."
                this._messageService.add({
                    severity: "error",
                    summary: "Greška",
                    detail,
                    life: 10000,
                })
            },
        })
    }
}
